import styled from "@emotion/styled/macro";
import {
  Button,
  LoadingIndicator,
  Checkbox,
} from "@sunrun/experience-ui-components";
import { ReactNode } from "react";
import { useMediaQuery } from "react-responsive";
import { mobileMaxWidth } from "components/atoms/GlobalStyles";
import { useCheckoutContext } from "checkout/providers/CheckoutContext";

type CheckoutFooterProps = {
  checkboxLabel?: string;
  checkboxChecked?: boolean;
  buttonText: string;
  children?: ReactNode;
  onClick?: () => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  testId?: string;
  onCheckboxChange?: (checked: boolean) => void;
};

const CheckoutFooter = ({
  checkboxLabel,
  checkboxChecked,
  onCheckboxChange,
  buttonText,
  children,
  onClick = () => {},
  isLoading = false,
  isDisabled = false,
  testId = "checkout-footer-button-continue",
}: CheckoutFooterProps) => {
  const { isNewHomes } = useCheckoutContext();
  const isMobile = useMediaQuery({ maxWidth: mobileMaxWidth });
  const buttonClicked = () => {
    onClick();
  };

  const buttonDisabled =
    isDisabled || (!!checkboxLabel?.length && !checkboxChecked);

  return (
    <CheckoutPageFooter isNewHomes={isNewHomes}>
      {checkboxLabel && (
        <ConfirmationTextContainer isNewHomes={isNewHomes}>
          <Checkbox
            role="checkbox"
            checked={checkboxChecked}
            onChange={(e) => onCheckboxChange?.(e.target.checked)}
            label={checkboxLabel}
          />
        </ConfirmationTextContainer>
      )}
      <CheckoutPageInnerFooter isNewHomes={isNewHomes}>
        {children}
        <ButtonContainer isMobile={isMobile}>
          <StyledButton
            isMobile={isMobile}
            data-testid={testId}
            size="md"
            onClick={buttonClicked}
            disabled={buttonDisabled}
          >
            {isLoading ? (
              <LoadingIndicator color="white" showLoadingMessage={false} />
            ) : (
              buttonText
            )}
          </StyledButton>
        </ButtonContainer>
      </CheckoutPageInnerFooter>
    </CheckoutPageFooter>
  );
};

const CheckoutPageFooter = styled.div<{ isNewHomes?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: ${({ isNewHomes }) => (isNewHomes ? "flex-end" : "normal")};

  @media (max-width: ${mobileMaxWidth}px) {
    flex-direction: column;
  }
`;
const ButtonContainer = styled.div<{ isMobile?: boolean }>`
  ${({ isMobile }) =>
    isMobile &&
    `
    width: 100%;
  `}
`;

const StyledButton = styled(Button)<{ isMobile?: boolean }>`
  width: ${({ isMobile }) => (isMobile ? "100%" : "fit-content")};
`;
const CheckoutPageInnerFooter = styled.div<{ isNewHomes?: boolean }>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;
  max-height: ${({ isNewHomes }) => (isNewHomes ? "fit-content" : "auto")};

  @media (max-width: ${mobileMaxWidth}px) {
    width: 100%;
    margin-left: 0;
  }
`;

const ConfirmationTextContainer = styled.div<{ isNewHomes?: boolean }>`
  flex: 1;
  min-width: 0;
  text-align: ${({ isNewHomes }) => (isNewHomes ? "left" : "center")};
  font-size: 16px;
  line-height: 24px;
  margin: ${({ isNewHomes }) => (isNewHomes ? "0px" : "auto")};
  margin-left: ${({ isNewHomes }) => (isNewHomes ? "0px" : "12px")};

  @media (max-width: ${mobileMaxWidth}px) {
    width: 100%;
    margin-bottom: 16px;
  }
`;

export { CheckoutFooter };
